<template>
  <div class="amendments">
    <div class="amendments__header filters-bar">
      <amendments-filter />
      <i-button
        v-if="getContractModulePerms('amendments', 'create')"
        type="success"
        @click="openAmendmentModal"
        >Создать ДС</i-button
      >
    </div>
    <inline-loader :loading="loading" v-if="loading && !nodata" />
    <div class="ms-nodata ms-text" v-else-if="!loading && nodata">
      Не удалось загрузить список ДС. Повторите, пожалуйста, попытку позже
    </div>
    <amendments-list
      @edit="handleEdit"
      :amendments="amendments"
      v-else-if="amendments.length"
    />
    <div v-else class="ms-nodata ms-text">ДС отсутствуют</div>

    <modal
      class-name="amendments-editor"
      v-model="amendmentModal"
      width="320"
      :styles="{ top: '40px' }"
      :mask-closable="false"
    >
      <div slot="header" class="amendments-editor__header">
        <icon size="16" type="md-create" class="ms-help" />
        <span style="margin-left: 4px" class="ms-help">
          {{ amendment.id ? "Редактирование" : "Создание" }} ДС
        </span>
      </div>
      <div class="amendments-editor__container">
        <div class="amendments-editor__info">
          <row
            class="amendments-editor__row"
            v-for="row in amendmentPropsRows"
            :key="row.prop"
          >
            <i-col span="24">
              <span class="ms-help label">{{ row.title }}</span>
              <template v-if="row.type === 'string'">
                <i-input
                  size="default"
                  v-model="amendment[row.prop]"
                  placeholder="12"
                />
              </template>
              <template v-else-if="row.type === 'date'">
                <date-picker
                  size="default"
                  format="dd.MM.yyyy"
                  placeholder="Выберите дату"
                  v-model="amendment[row.prop]"
                />
              </template>
              <template v-else-if="row.type === 'select'">
                <i-select
                  v-model="amendment[row.prop]"
                  clearable
                  multiple
                  :max-tag-count="1"
                  size="default"
                >
                  <i-option
                    v-for="item in values[row.value]"
                    :value="item.id"
                    :key="item.id"
                  >
                    {{ item.name }}
                  </i-option>
                </i-select>
              </template>
              <template v-else-if="row.type === 'mandatory_files'">
                <contract-mandatory-files
                  v-if="amendmentModal"
                  :contract="amendment"
                  :all-file-types="values.file_types"
                />
              </template>
            </i-col>
          </row>
        </div>
      </div>
      <div slot="footer" class="amendments-editor__footer">
        <i-button
          size="default"
          :disabled="!amendment.id"
          type="error"
          @click="handleDelete"
        >
          Удалить
        </i-button>

        <i-button
          size="default"
          :disabled="!isSaveAvailable"
          type="success"
          @click="saveAmendment"
          :loading="saving"
        >
          Сохранить
        </i-button>
      </div>
    </modal>

    <modal :z-index="2000" v-model="deleteModal" width="360">
      <p slot="header" style="color: #f60; text-align: center">
        <icon type="ios-information-circle" />
        <span style="margin-left: 4px">Удаление ДС</span>
      </p>
      <div style="text-align: center">
        <p class="ms-text">Вы уверены, что хотите удалить данное ДС?</p>
      </div>
      <div slot="footer">
        <i-button
          type="error"
          size="default"
          long
          :loading="deleteLoading"
          @click="deleteAmendment"
        >
          Удалить
        </i-button>
      </div>
    </modal>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep'
import moment from 'moment'
import AmendmentsList from '@/components/amendments/amendments-list'
import AmendmentsFilter from '@/components/filters/amendments'
import InlineLoader from '@/components/loaders/inline-loader'
import ContractMandatoryFiles from '@/components/contract-editor/helpers/contract-mandatory-files'
import { getAmendmentsUrl } from '@/config/filters'
import {
  amendmentPropsRows,
  amendmentsComplexFields
} from '@/config/amendments'
import {
  formatDefaultDate,
  modifyComplexData,
  getNewComplexValues
} from '@/utils'

const amendment = {
  id: null,
  date: '',
  number: null,
  amendment_amendment_types: [],
  mandatory_file_types: []
}

export default {
  components: {
    AmendmentsList,
    AmendmentsFilter,
    InlineLoader,
    ContractMandatoryFiles
  },
  data () {
    return {
      loading: false,
      nodata: false,
      values: {
        amendment_types: [],
        file_types: []
      },
      initial: {
        amendment_types: [],
        file_types: []
      },
      amendments: [],
      amendmentModal: false,
      commonChanges: false,
      roadmapChanges: false,
      saving: false,
      amendment: { ...amendment },
      deleteLoading: false,
      deleteModal: false,
      deletingId: null,
      amendmentPropsRows
    }
  },
  computed: {
    isSaveAvailable () {
      return this.amendment.number && this.amendment.date
    }
  },
  watch: {
    '$store.state.filters.amendments': {
      handler: function () {
        this.getAmendments(true)
      },
      deep: true
    }
  },
  methods: {
    async getBookInfo () {
      try {
        this.values.amendment_types = await this.$store.dispatch(
          'GET_REQUEST',
          {
            url: 'objectInfo/amendment_types'
          }
        )

        const fileTypes = await this.$store.dispatch('GET_REQUEST', {
          url: 'objectInfo/file_types'
        })
        this.values.file_types = fileTypes
          .filter((t) => t.object_name === 'contracts')
          .sort((a, b) => a.index - b.index)
      } catch (error) {
        console.log(error)
      }
    },
    async getAmendments (noloading) {
      const { id } = this.$route.params

      try {
        if (!noloading) this.loading = true
        const url = getAmendmentsUrl(
          `page/amendment?contract_id=${id}`,
          this.$store.state.filters.amendments
        )
        this.amendments = await this.$store.dispatch('GET_REQUEST', { url })
        this.loading = false
      } catch (error) {
        console.log(error)
        this.loading = false
        this.nodata = true
      }
    },
    handleEdit (data) {
      Object.keys(this.amendment).forEach((key) => {
        let value

        switch (key) {
          case 'date':
            value = formatDefaultDate(data[key])
            break
          case 'amendment_amendment_types':
            this.initial.amendment_types = data[key]
            value = data[key].map((t) => t.amendment_type_id)
            break
          case 'mandatory_file_types':
            this.initial.file_types = data[key]
            value = data[key].map((t) => t.file_type_id)
            break
          default:
            value = data[key]
        }

        this.$set(this.amendment, key, value)
      })
      this.amendmentModal = true
    },
    handleDelete () {
      this.deletingId = this.amendment.id
      this.deleteModal = true
    },
    openAmendmentModal () {
      this.$set(this, 'amendment', { ...amendment })
      this.amendmentModal = !this.amendmentModal
    },
    getAmendmentData () {
      const data = cloneDeep(this.amendment)

      data.date = data.id
        ? moment(data.date, 'DD.MM.YYYY').format()
        : moment(data.date).format()
      data.contract_id = this.$route.params.id

      if (data.id) {
        data.amendment_amendment_types = getNewComplexValues(
          data.amendment_amendment_types,
          this.initial.amendment_types,
          'amendment_type_id'
        )

        data.mandatory_file_types = getNewComplexValues(
          data.mandatory_file_types,
          this.initial.file_types,
          'file_type_id'
        )
      } else {
        amendmentsComplexFields.forEach(([prop, idProp]) => {
          modifyComplexData(data, prop, idProp)
        })

        delete data.id
      }

      return data
    },
    async saveAmendment () {
      try {
        this.saving = true
        const url = `page/amendment`
        const data = this.getAmendmentData()

        await this.$store.dispatch('POST_REQUEST', { url, data })
        await this.getAmendments()
        this.$Message.success('Сохранение данных выполнено успешно')
        this.amendmentModal = false
        this.$set(this, 'amendment', { ...amendment })
        this.saving = false
      } catch (error) {
        console.log(error)
        this.$Message.error('Произошла ошибка при сохранении данных')
        this.saving = false
      }
    },
    async deleteAmendment () {
      if (!this.deletingId) return

      try {
        this.deleteLoading = true
        const url = `page/amendment?id=${this.deletingId}`
        await this.$store.dispatch('DELETE_REQUEST', { url })
        await this.getAmendments()
        this.$Message.success('Удаление данных выполнено успешно')
        this.deleteLoading = false
        this.deletingId = null
        this.deleteModal = false
        this.amendmentModal = false
      } catch (error) {
        console.log(error)
        this.$Message.error('Произошла ошибка при удалении данных')
        this.deleteLoading = false
      }
    }
  },
  created () {
    this.getBookInfo()
    this.getAmendments()
  }
}
</script>

<style lang="less">
.amendments-editor {
  &__row {
    &:not(:first-child) {
      margin-top: 8px;
    }

    .ivu-date-picker {
      width: 100%;
    }
  }

  &__footer {
    display: flex;
    justify-content: space-between;
  }
}
</style>
